import React from 'react';
import { Box, IconButton, makeStyles } from '@material-ui/core';
import { FileText, X } from 'react-feather';
import type { TOutboundMultimediaItem } from 'src/views/OmniChannel/types/outboundMultimedia';
import { isImagePreview } from 'src/views/OmniChannel/utils/outboundMultimedia';

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        alignItems: 'flex-end',
        gap: theme.spacing(1.5),
        padding: theme.spacing(1.25, 1.5),
        backgroundColor: theme.palette.background.paper,
        borderTop: `1px solid ${theme.palette.divider}`,
        overflowX: 'auto',
        overflowY: 'hidden',
    },
    itemWrapper: {
        position: 'relative',
        flexShrink: 0,
        paddingTop: 10,
    },
    tile: {
        width: 56,
        height: 56,
        borderRadius: 8,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.action.hover,
        cursor: 'pointer',
        overflow: 'hidden',
        transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
        '&:hover': {
            borderColor: theme.palette.primary.main,
            boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
        },
    },
    thumbnail: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
    },
    fileIcon: {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.palette.text.secondary,
    },
    removeButton: {
        position: 'absolute',
        top: 0,
        right: -2,
        width: 20,
        height: 20,
        padding: 0,
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[1],
        zIndex: 1,
        '&:hover': {
            backgroundColor: theme.palette.error.light,
            color: theme.palette.error.contrastText,
        },
    },
}));

type TOutboundMultimediaPreviewProps = {
    items: TOutboundMultimediaItem[];
    onRemove: (itemId: string) => void;
    onPreview: (item: TOutboundMultimediaItem) => void;
};

export const OutboundMultimediaPreview = ({
    items,
    onRemove,
    onPreview,
}: TOutboundMultimediaPreviewProps) => {
    const classes = useStyles();

    if (!items.length) return null;

    return (
        <Box className={classes.root}>
            {items.map((item) => {
                const showImage = isImagePreview(item.previewUrl, item.contentType);

                return (
                    <Box key={item.id} className={classes.itemWrapper}>
                        <IconButton
                            className={classes.removeButton}
                            size="small"
                            aria-label="remove-outbound-multimedia"
                            onClick={(event) => {
                                event.stopPropagation();
                                onRemove(item.id);
                            }}
                        >
                            <X size={12} />
                        </IconButton>

                        <Box className={classes.tile}>
                            <Box
                                role="button"
                                tabIndex={0}
                                aria-label={item.name}
                                onClick={() => onPreview(item)}
                                onKeyDown={(event) => {
                                    if (
                                        event.key === 'Enter' ||
                                        event.key === ' '
                                    ) {
                                        event.preventDefault();
                                        onPreview(item);
                                    }
                                }}
                                style={{ width: '100%', height: '100%' }}
                            >
                                {showImage ? (
                                    <img
                                        className={classes.thumbnail}
                                        src={item.previewUrl}
                                        alt={item.name}
                                    />
                                ) : (
                                    <Box className={classes.fileIcon}>
                                        <FileText size={22} />
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};
